import type {
    ProductGroupResponse,
    RepairServiceListResponse,
    ShopListResponse,
    AppointmentListResponse,
    BookingCreateResponse,
    StartBookingResponse,
    NewRepairBooking,
    BookingDetailsResponse,
    BookingRescheduleResponse,
    ExistingRepairBooking,
    BookingSearchParams,
    BookingSearchResponse,
    CustomerLineOptionsResponse,
    SessionType,
    ServiceAvailabilityResponse,
} from '../models/repair-bookings';

const apiPathPrefix = '/api';

type ApiErrorCode = number & { readonly __brand: unique symbol };

interface ApiErrorResponse {
    code?: ApiErrorCode;
    message?: string;
}

const apiPost = async <T>(
    path: string,
    csrfToken: string,
    body: Record<string, unknown>
): Promise<T> => {
    const response = await fetch(`${apiPathPrefix}${path}`, {
        method: 'POST',
        headers: commonHeaders(csrfToken),
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(await getErrorMessage(response));
    }
    return (await response.json()) as T;
};

const getErrorMessage = async (
    response: Response,
    defaultMessage: string = '送信に失敗しました。しばらくお待ちいただいてからもう一度操作を行ってください。'
): Promise<string> => {
    try {
        const error = (await response.json()) as ApiErrorResponse;
        return error.message ?? defaultMessage;
    } catch {
        return defaultMessage;
    }
};

const commonHeaders = (csrfToken: string) => ({
    'Content-Type': 'application/json',
    'X-CSRFToken': csrfToken,
});

export const fetchAvailability = async (
    csrfToken: string
): Promise<ServiceAvailabilityResponse> => {
    return await apiPost('/availability/', csrfToken, {});
};

export const startSession = async (
    csrfToken: string,
    type: SessionType
): Promise<StartBookingResponse> => {
    return await apiPost('/booking_sessions/new/', csrfToken, { type: type });
};

export const fetchProductGroup = async (
    csrfToken: string,
    sessionId: string,
    groupKey: string,
    shopId: string | null
): Promise<ProductGroupResponse> => {
    return await apiPost(`/products/groups/${groupKey}/`, csrfToken, {
        session_id: sessionId,
        shop_id: shopId,
    });
};

export const fetchRepairServices = async (
    csrfToken: string,
    sessionId: string,
    productId: number
): Promise<RepairServiceListResponse> => {
    return await apiPost('/services/repair_services/', csrfToken, {
        session_id: sessionId,
        product_id: productId,
    });
};

export const fetchCustomerOptions = async (
    csrfToken: string
): Promise<CustomerLineOptionsResponse> => {
    return await apiPost('/customers/options/', csrfToken, {});
};

export const fetchShops = async (
    csrfToken: string,
    sessionId?: string,
    productId?: number
): Promise<ShopListResponse> => {
    return await apiPost('/shops/', csrfToken, {
        session_id: sessionId,
        product_id: productId,
    });
};

export const fetchTimeSlots = async (
    csrfToken: string,
    shopId: string,
    sessionId: string,
    bookingToken?: string,
    productId?: number,
    repairServiceIds?: number[]
): Promise<AppointmentListResponse> => {
    return await apiPost(`/shops/${shopId}/timeslots/`, csrfToken, {
        session_id: sessionId,
        booking_token: bookingToken,
        product_id: productId,
        repair_service_ids: repairServiceIds,
    });
};

export const createBooking = async (
    csrfToken: string,
    sessionId: string,
    booking: NewRepairBooking
): Promise<BookingCreateResponse> => {
    const { product, repairOptions, shop, selectedSlot, personalInfo } = booking;
    return await apiPost('/bookings/new/', csrfToken, {
        session_id: sessionId,
        product_id: product!.id,
        repair_service_ids: repairOptions.map((s) => s.id),
        shop_id: shop!.shop_id,
        appointment_id: selectedSlot!.appointment_id,
        start_at: selectedSlot!.start_at,
        last_name: personalInfo.last_name,
        first_name: personalInfo.first_name,
        last_name_pronunciation: personalInfo.last_name_pronunciation,
        first_name_pronunciation: personalInfo.first_name_pronunciation,
        phone_number: personalInfo.phone_number,
        alt_phone_number: personalInfo.alt_phone_number,
        email: personalInfo.email,
        notes: personalInfo.notes,
    });
};

export const createConsoleBooking = async (
    csrfToken: string,
    sessionId: string,
    booking: NewRepairBooking
): Promise<BookingCreateResponse> => {
    const { product, repairOptions, shop, selectedSlot, personalInfo } = booking;
    return await apiPost('/console/bookings/new/', csrfToken, {
        session_id: sessionId,
        product_id: product!.id,
        repair_service_ids: repairOptions.map((s) => s.id),
        shop_id: shop!.shop_id,
        appointment_id: selectedSlot!.appointment_id,
        start_at: selectedSlot!.start_at,
        last_name: personalInfo.last_name,
        first_name: personalInfo.first_name,
        last_name_pronunciation: personalInfo.last_name_pronunciation,
        first_name_pronunciation: personalInfo.first_name_pronunciation,
        phone_number: personalInfo.phone_number,
        alt_phone_number: personalInfo.alt_phone_number,
        email: personalInfo.email,
        notes: personalInfo.notes,
        birthday: personalInfo.birthday!,
        is_warranty_member: personalInfo.is_warranty_member!,
        line_status: personalInfo.line_status!,
        subscriber_code: personalInfo.subscriber_code!,
    });
};

export const fetchBookingDetails = async (
    csrfToken: string,
    bookingToken: string
): Promise<BookingDetailsResponse> => {
    return await apiPost(`/bookings/${bookingToken}/`, csrfToken, {});
};

export const updateBooking = async (
    csrfToken: string,
    sessionId: string,
    booking: ExistingRepairBooking
): Promise<BookingRescheduleResponse> => {
    return await apiPost(`/bookings/${booking.bookingToken}/reschedule/`, csrfToken, {
        session_id: sessionId,
        shop_id: booking.shop?.shop_id,
        appointment_id: booking.selectedSlot?.appointment_id,
        start_at: booking.selectedSlot?.start_at,
    });
};

export const updateConsoleBooking = async (
    csrfToken: string,
    sessionId: string,
    booking: ExistingRepairBooking
): Promise<BookingRescheduleResponse> => {
    const params = {
        session_id: sessionId,
        shop_id: booking.shop?.shop_id,
        appointment_id: booking.selectedSlot?.appointment_id,
        start_at: booking.selectedSlot?.start_at,
    };
    return await apiPost(`/console/bookings/${booking.bookingToken}/update/`, csrfToken, {
        ...params,
        ...booking.personalInfo,
    });
};

export const cancelBooking = async (csrfToken: string, bookingToken: string) => {
    return await apiPost(`/bookings/${bookingToken}/cancel/`, csrfToken, {});
};

export const consoleCancelBooking = async (csrfToken: string, bookingToken: string) => {
    return await apiPost(`/console/bookings/${bookingToken}/cancel/`, csrfToken, {});
};

export const searchBookings = async (
    csrfToken: string,
    params: BookingSearchParams
): Promise<BookingSearchResponse> => {
    return await apiPost('/bookings/search/', csrfToken, params);
};

export const apiCallWithAlertOnError = <T>(
    apiCall: () => Promise<T>
): (() => Promise<T | undefined>) => {
    return async () => {
        try {
            return await apiCall();
        } catch (e) {
            alert((e as Error).message);
            return undefined;
        }
    };
};
